import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { useMode } from '../../../../../../../hooks/useMode'
import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { MasterDataManagementCompanyCodes } from '../../../../../../ui-config/data'
import { CompanyCode, CustomFieldDefinition } from '../../../../suppliers'
import { FormValues } from '../common'
import { AdditionalProperties } from './AdditionalProperties'

interface FormProps {
  formik: FormikProps<FormValues>
  isFormFieldValid: any
  getFormErrorMessage: (name: keyof FormValues) => false | JSX.Element
  getNestedFormErrorMessage: (
    index: number,
    fieldName: string,
  ) => false | JSX.Element
  config: MasterDataManagementCompanyCodes['edit']
  handleClose: () => void
  isRequestRunning: boolean
  availableCustomFieldsOptions?: CustomFieldDefinition[]
  allCompanyCodes?: CompanyCode[]
}

export const EditCompanyCodeView: React.FC<FormProps> = ({
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  getNestedFormErrorMessage,
  config,
  handleClose,
  isRequestRunning,
  availableCustomFieldsOptions,
  allCompanyCodes,
}) => {
  const { t } = useTranslation()
  const mode = useMode()

  const [isCustomFieldsEditing, setIsCustomFieldsEditing] =
    React.useState(false)

  // IsStandard
  // Check if this is already a standard company code
  const isExistingStandard =
    allCompanyCodes?.some(
      existingCompanyCode =>
        existingCompanyCode.IsStandard &&
        existingCompanyCode.Id === formik.values.id,
    ) || false

  const isStandardComponent = (
    <InputSwitch
      checked={formik.values.isStandard}
      id="isStandard"
      onChange={formik.handleChange}
      disabled={isExistingStandard}
    />
  )

  const isStandardErrorMsg = <div>{getFormErrorMessage('isStandard')}</div>

  const isStandardWrapper = (
    <ConfigurableComponent
      config={config}
      component={isStandardComponent}
      errorMsg={isStandardErrorMsg}
    />
  )

  // Display id
  const displayIdLabel = (
    <label
      htmlFor="displayId"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('displayId'),
      })}
    ></label>
  )

  const displayIdComponent = (
    <InputText
      id="displayId"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('displayId'),
      })}
      value={formik.values.displayId}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'edit' || mode === 'info'}
    />
  )

  const displayIdErrorMsg = <div>{getFormErrorMessage('displayId')}</div>

  const displayIdWrapper = (
    <ConfigurableComponent
      config={config}
      component={displayIdComponent}
      label={displayIdLabel}
      errorMsg={displayIdErrorMsg}
    />
  )

  // Company
  const companyLabel = (
    <label
      htmlFor="company"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('company'),
      })}
    ></label>
  )

  const companyComponent = (
    <InputText
      id="company"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('company'),
      })}
      value={formik.values.company}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const companyErrorMsg = <div>{getFormErrorMessage('company')}</div>

  const companyWrapper = (
    <ConfigurableComponent
      config={config}
      component={companyComponent}
      label={companyLabel}
      errorMsg={companyErrorMsg}
    />
  )

  // Tax Number
  const taxNumberLabel = (
    <label
      htmlFor="taxNumber"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('taxNumber'),
      })}
    ></label>
  )

  const taxNumberComponent = (
    <InputText
      id="taxNumber"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('taxNumber'),
      })}
      value={formik.values.taxNumber}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const taxNumberErrorMsg = <div>{getFormErrorMessage('taxNumber')}</div>

  const taxNumberWrapper = (
    <ConfigurableComponent
      config={config}
      component={taxNumberComponent}
      label={taxNumberLabel}
      errorMsg={taxNumberErrorMsg}
    />
  )

  // Vat id
  const salesTaxIDLabel = (
    <label
      htmlFor="salesTaxID"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('salesTaxID'),
      })}
    ></label>
  )

  const salesTaxIDComponent = (
    <InputText
      id="salesTaxID"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('salesTaxID'),
      })}
      value={formik.values.salesTaxID}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const salesTaxIDErrorMsg = <div>{getFormErrorMessage('salesTaxID')}</div>

  const salesTaxIDWrapper = (
    <ConfigurableComponent
      config={config}
      component={salesTaxIDComponent}
      label={salesTaxIDLabel}
      errorMsg={salesTaxIDErrorMsg}
    />
  )

  // Street
  const streetLabel = (
    <label
      htmlFor="street"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('street'),
      })}
    ></label>
  )

  const streetComponent = (
    <InputText
      id="street"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('street'),
      })}
      value={formik.values.street}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const streetErrorMsg = <div>{getFormErrorMessage('street')}</div>

  const streetWrapper = (
    <ConfigurableComponent
      config={config}
      component={streetComponent}
      label={streetLabel}
      errorMsg={streetErrorMsg}
    />
  )

  // Postal Code
  const postalCodeLabel = (
    <label
      htmlFor="postalCode"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('postalCode'),
      })}
    ></label>
  )

  const postalCodeComponent = (
    <InputText
      id="postalCode"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('postalCode'),
      })}
      value={formik.values.postalCode}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const postalCodeErrorMsg = <div>{getFormErrorMessage('postalCode')}</div>

  const postalCodeWrapper = (
    <ConfigurableComponent
      config={config}
      component={postalCodeComponent}
      label={postalCodeLabel}
      errorMsg={postalCodeErrorMsg}
    />
  )

  // City
  const cityLabel = (
    <label
      htmlFor="city"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('city'),
      })}
    ></label>
  )

  const cityComponent = (
    <InputText
      id="city"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('city'),
      })}
      value={formik.values.city}
      onChange={formik.handleChange}
      disabled={isRequestRunning || mode === 'info'}
    />
  )

  const cityErrorMsg = <div>{getFormErrorMessage('city')}</div>

  const cityWrapper = (
    <ConfigurableComponent
      config={config}
      component={cityComponent}
      label={cityLabel}
      errorMsg={cityErrorMsg}
    />
  )

  // Country
  const countryLabel = (
    <label
      htmlFor="country"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('country'),
      })}
    ></label>
  )

  const countryComponent = (
    <InputText
      id="country"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('country'),
      })}
      value={formik.values.country}
      onChange={formik.handleChange}
      disabled
    />
  )

  const countryErrorMsg = <div>{getFormErrorMessage('country')}</div>

  const countryWrapper = (
    <ConfigurableComponent
      config={config}
      component={countryComponent}
      label={countryLabel}
      errorMsg={countryErrorMsg}
    />
  )

  // Footer
  const footer = () => {
    if (mode === 'info')
      return (
        <Button
          label={t(
            'masterDataManagement.companyCodes.table.dialog.close.label',
          )}
          icon="pi pi-times"
          className="p-button-text w-full md:w-auto mb-1 md:mb-0"
          onClick={handleClose}
          disabled={isRequestRunning}
        />
      )

    const saveButtonLabel =
      mode === 'add'
        ? t('masterDataManagement.companyCodes.table.dialog.add.label')
        : t('masterDataManagement.companyCodes.table.dialog.save.label')

    return (
      <>
        <Button
          label={t(
            'masterDataManagement.companyCodes.table.dialog.cancel.label',
          )}
          icon="pi pi-times"
          className="p-button-text w-full md:w-auto mb-1 md:mb-0"
          onClick={handleClose}
          disabled={isRequestRunning}
        />
        <Button
          label={saveButtonLabel}
          icon={'pi pi-check'}
          className="w-full md:w-auto"
          onClick={formik.submitForm}
          type="submit"
          disabled={!formik.dirty || isCustomFieldsEditing}
          loading={isRequestRunning}
        />
      </>
    )
  }

  const header = () => {
    if (mode === 'add')
      return t('masterDataManagement.companyCodes.table.dialog.title.add')

    if (mode === 'edit')
      return t('masterDataManagement.companyCodes.table.dialog.title.edit', {
        company: formik.values.company,
      })

    if (mode === 'info')
      return t('masterDataManagement.companyCodes.table.dialog.title.info', {
        company: formik.values.company,
      })
  }

  return (
    <Dialog
      header={header()}
      visible
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      <div className="formgrid grid">
        {isStandardWrapper}
        <label
          htmlFor="isStandard"
          className={classNames('ml-2', {
            'p-error': !isFormFieldValid('isStandard'),
          })}
        >
          {t(
            'masterDataManagement.companyCodes.table.dialog.form.billingInformation.isStandard.label',
          )}
        </label>
      </div>

      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.companyCodes.table.dialog.form.billingInformation.title',
          )}
        </h4>
        <div className="col-12 md:col-6 lg:col-3">{displayIdWrapper}</div>

        <div className="col-12 md:col-6 lg:col-3">{companyWrapper}</div>

        <div className="col-12 md:col-6 lg:col-3">{taxNumberWrapper}</div>

        <div className="col-12 md:col-6 lg:col-3">{salesTaxIDWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.companyCodes.table.dialog.form.billingAddress.title',
          )}
        </h4>

        <div className="col-12 md:col-6 lg:col-3">{streetWrapper}</div>

        <div className="col-12 md:col-6 lg:col-3">{postalCodeWrapper}</div>

        <div className="col-12 md:col-6 lg:col-3">{cityWrapper}</div>

        <div className="col-12 md:col-6 lg:col-3">{countryWrapper}</div>
      </div>

      <div className="formgrid grid">
        <h4 className="col-12">
          {t(
            'masterDataManagement.companyCodes.table.dialog.form.additionalProperties.title',
          )}
        </h4>
        <AdditionalProperties
          formik={formik}
          config={config.additionalProperties}
          getNestedFormErrorMessage={getNestedFormErrorMessage}
          availableCustomFieldsOptions={availableCustomFieldsOptions}
          onEditingModeChange={setIsCustomFieldsEditing}
        />
      </div>
    </Dialog>
  )
}
